import { AccountMenu } from 'components/ui/AccountMenu';
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import { useTranslation } from 'react-i18next';
import { Business, DevicesOther, SupportAgent } from '@mui/icons-material';
import ApiIcon from '@mui/icons-material/Api';
import FeedbackIcon from '@mui/icons-material/Feedback';
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import {
  Badge,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import PriceChangeRoundedIcon from '@mui/icons-material/PriceChangeRounded';

import { rq } from 'http/apiRoutes';
import { useGet } from 'http/useApi';
import { useQueryClient } from 'react-query';
import { useSetAtom } from 'jotai';

import { useAppDispatch, useAppState } from 'context/AppContext';
import { DashboardStats } from 'pages/PageLayout';
import userAtom from 'data/atoms/userAtom';

const variables = {
  PHOTO_URL: process.env.PUBLIC_URL + '/img/',
};

interface SidebarProps {
  dashboardStats: DashboardStats;
  sidebarWidth?: number;
}

export const Sidebar: React.FC<SidebarProps> = ({
  dashboardStats,
  sidebarWidth,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const setUserAtom = useSetAtom(userAtom);

  const [email, setEmail] = useState('');
  const stats = { ...dashboardStats };

  const dispatch = useAppDispatch();
  const state = useAppState();
  const { tenantId, authMe, user } = state;

  const tenantIdFrom = (t: any) => t?.tenantId || t?.id || t?.slug || '';

  // 1) auth/me når email er satt
  const canCallAuth = !!email;
  const { data: authData } = useGet(rq.authMe(canCallAuth));

  useEffect(() => {
    dispatch({ type: 'SET_EMAIL', payload: email || null });
  }, [email, dispatch]);

  // 2) lagre authMe + default tenant første gang
  useEffect(() => {
    if (!authData) return;

    dispatch({ type: 'SET_AUTH_ME', payload: authData });
    if (authData?.user) {
      dispatch({ type: 'SET_USER', payload: authData.user });
      setUserAtom(authData.user);
    }

    const currentTenant = authData.tenants?.find((t: any) => t?.isCurrent);
    const firstTenant = authData.tenants?.[0];
    const chosenTenantId =
      tenantIdFrom(currentTenant) || tenantIdFrom(firstTenant);

    if (chosenTenantId && !tenantId) {
      dispatch({ type: 'SET_TENANT', payload: chosenTenantId });
      dispatch({ type: 'SET_LOGGED_IN', payload: true });
    }
  }, [authData, tenantId, dispatch, setUserAtom]);

  // Øo. Tenant switch handler til AccountMenu
  const handleTenantChange = useCallback(
    (newTenantId: string) => {
      if (!newTenantId || newTenantId === tenantId) return;

      dispatch({ type: 'SET_TENANT', payload: newTenantId });
      dispatch({ type: 'RESET_TENANT_STATE' });

      // tøm cache så alle tenant-spesifikke queries henter på nytt
      queryClient.clear();
      window.location.reload();
    },
    [tenantId, dispatch, queryClient]
  );

  return (
    <Paper
      variant='elevation3'
      sx={{
        height: '95%',
        p: 2,
        mr: 1,
      }}
    >
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Link to='/'>
          <Grid item sx={{ display: 'flex', justifyContent: 'center' }}>
            <img height='100' src={variables.PHOTO_URL + 'iBluu.png'} alt='' />
          </Grid>
        </Link>
      </Grid>

      {/* Øo. Mate AccountMenu med tenants + aktiv tenant + bytte-funksjon */}
      <Divider sx={{ backgroundColor: 'black' }} />

      <Grid
        item
        xs={12}
        sx={{
          height: 80,
          mb: 2,
        }}
      >
        <AccountMenu
          sidebarWidth={sidebarWidth}
          setEmail={setEmail}
          url={user?.url ?? null}
          tenants={authMe?.tenants ?? []}
          activeTenantId={tenantId}
          onTenantChange={handleTenantChange}
        />
      </Grid>

      <Divider sx={{ backgroundColor: 'primary.lighter' }} />

      <List sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <ListItem
          component={Link}
          to='/companies'
          sx={{
            m: 0,
            p: 0,
            color: 'primary.text',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            textDecoration: 'none !important',
            '&:hover': {
              textDecoration: 'none !important',
            },
            '&:visited': {
              color: 'primary.text !important',
              textDecoration: 'none !important',
            },
          }}
        >
          <ListItemIcon>
            <Business
              sx={{
                color: stats.newCompanies > 0 ? 'secondary.main' : 'default',
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={
              <Grid
                container
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: '0.9rem', fontWeight: 500, flexGrow: 1 }}
                >
                  {t('sidebar.companies')}
                </Typography>
                <Chip
                  variant='filled'
                  color={stats.newCompanies > 0 ? 'secondary' : 'default'}
                  label={stats.companies}
                />
              </Grid>
            }
          />
        </ListItem>

        <Divider sx={{ backgroundColor: 'primary.lighter' }} />

        <ListItem
          component={Link}
          to='/tickets'
          sx={{
            m: 0,
            p: 0,
            color: 'primary.text',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            textDecoration: 'none !important',
            '&:hover': {
              textDecoration: 'none !important',
            },
            '&:visited': {
              color: 'primary.text !important',
              textDecoration: 'none !important',
            },
          }}
        >
          <ListItemIcon>
            <SupportAgent
              sx={{
                color: stats.newTickets > 0 ? 'secondary.main' : 'default',
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={
              <Grid
                container
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: '0.9rem', fontWeight: 500, flexGrow: 1 }}
                >
                  {t('sidebar.tickets')}
                </Typography>
                <Chip
                  variant='filled'
                  color={stats.newTickets > 0 ? 'secondary' : 'default'}
                  label={stats.tickets}
                />
              </Grid>
            }
          />
        </ListItem>

        <Divider sx={{ backgroundColor: 'primary.lighter' }} />

        <ListItem
          component={Link}
          to='/orders'
          sx={{
            m: 0,
            p: 0,
            color: 'primary.text',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            textDecoration: 'none !important',
            '&:hover': {
              textDecoration: 'none !important',
            },
            '&:visited': {
              color: 'primary.text !important',
              textDecoration: 'none !important',
            },
          }}
        >
          <ListItemIcon>
            <ReceiptRoundedIcon
              sx={{
                color: stats.newOrders > 0 ? 'secondary.main' : 'default',
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={
              <Grid
                container
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: '0.9rem', fontWeight: 500, flexGrow: 1 }}
                >
                  {t('sidebar.orders')}
                </Typography>
                <Chip
                  variant='filled'
                  color={stats.newOrders > 0 ? 'secondary' : 'default'}
                  label={stats.orders}
                />
              </Grid>
            }
          />
        </ListItem>

        <Divider sx={{ backgroundColor: 'primary.lighter' }} />

        <ListItem
          component={Link}
          to='/products'
          sx={{
            m: 0,
            p: 0,
            color: 'primary.text',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            textDecoration: 'none !important',
            '&:hover': {
              textDecoration: 'none !important',
            },
            '&:visited': {
              color: 'primary.text !important',
              textDecoration: 'none !important',
            },
          }}
        >
          <ListItemIcon>
            <DevicesOther
              sx={{
                color: stats.newProducts > 0 ? 'secondary.main' : 'default',
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={
              <Grid
                container
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: '0.9rem', fontWeight: 500, flexGrow: 1 }}
                >
                  {t('sidebar.products')}
                </Typography>
                <Chip
                  variant='filled'
                  color={stats.newProducts > 0 ? 'secondary' : 'default'}
                  label={stats.products}
                />
              </Grid>
            }
          />
        </ListItem>

        <Divider sx={{ backgroundColor: 'primary.lighter' }} />

        <ListItem
          component={Link}
          to='/BillingView'
          sx={{
            m: 0,
            p: 0,
            color: 'primary.text',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            textDecoration: 'none !important',
            '&:hover': {
              textDecoration: 'none !important',
            },
            '&:visited': {
              color: 'primary.text !important',
              textDecoration: 'none !important',
            },
          }}
        >
          <ListItemIcon>
            <PriceChangeRoundedIcon
              sx={{
                color: stats.newInvoices > 0 ? 'secondary.main' : 'default',
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={
              <Grid
                container
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: '0.9rem', fontWeight: 500, flexGrow: 1 }}
                >
                  {t('sidebar.billing')}
                </Typography>
                <Chip
                  variant='filled'
                  color={stats.newInvoices > 0 ? 'secondary' : 'default'}
                  label={stats.invoices}
                />
              </Grid>
            }
          />
        </ListItem>

        <Divider sx={{ backgroundColor: 'primary.lighter' }} />

        <ListItem
          component={Link}
          to='/feedback'
          sx={{
            m: 0,
            p: 0,
            color: 'primary.text',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            textDecoration: 'none !important',
            '&:hover': {
              textDecoration: 'none !important',
            },
            '&:visited': {
              color: 'primary.text !important',
              textDecoration: 'none !important',
            },
          }}
        >
          <ListItemIcon>
            <FeedbackIcon
              sx={{
                color: stats.newFeedbacks > 0 ? 'secondary.main' : 'default',
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={
              <Grid
                container
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: '0.9rem', fontWeight: 500, flexGrow: 1 }}
                >
                  {t('sidebar.feedback')}
                </Typography>
                <Chip
                  variant='filled'
                  color={stats.newFeedbacks > 0 ? 'secondary' : 'default'}
                  label={stats.feedbacks}
                />
              </Grid>
            }
          />
        </ListItem>

        <Divider sx={{ backgroundColor: 'primary.lighter' }} />

        <ListItem
          component={Link}
          to='/setup'
          sx={{
            m: 0,
            p: 0,
            color: 'primary.text',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            textDecoration: 'none !important',
            '&:hover': {
              textDecoration: 'none !important',
            },
            '&:visited': {
              color: 'primary.text !important',
              textDecoration: 'none !important',
            },
          }}
        >
          <ListItemIcon>
            <SettingsRoundedIcon />
          </ListItemIcon>
          <ListItemText primary={t('sidebar.settings')} />
        </ListItem>

        <Divider sx={{ backgroundColor: 'primary.lighter' }} />

        <ListItem
          component={Link}
          to='/modules'
          sx={{
            m: 0,
            p: 0,
            color: 'primary.text',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            textDecoration: 'none !important',
            '&:hover': {
              textDecoration: 'none !important',
            },
            '&:visited': {
              color: 'primary.text !important',
              textDecoration: 'none !important',
            },
          }}
        >
          <ListItemIcon>
            <ApiIcon />
          </ListItemIcon>
          <ListItemText primary={t('Settings.SetupNav.Modules')} />
        </ListItem>
      </List>

      <Divider sx={{ backgroundColor: 'black' }} />
    </Paper>
  );
};
